import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export interface SuggestedPrompt {
  id: string;
  text: string;
}

interface SuggestedPromptsProps {
  prompts: SuggestedPrompt[];
  onPromptClick: (prompt: SuggestedPrompt) => void;
  disabled?: boolean;
}

const PromptChip = styled(Chip)(({ theme }) => ({
  cursor: 'pointer',
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: 16,
  padding: theme.spacing(0.5, 0),
  height: 'auto',
  transition: 'all 0.2s ease-in-out',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem',
    lineHeight: 1.4,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[2],
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

/**
 * Reusable component for displaying suggested prompts in a chat interface.
 * Designed to be extensible for future dynamic prompt suggestions based on conversation context.
 */
export function SuggestedPrompts({ prompts, onPromptClick, disabled = false }: SuggestedPromptsProps) {
  if (prompts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={500}>
          Suggested questions
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {prompts.map((prompt) => (
          <PromptChip
            key={prompt.id}
            label={prompt.text}
            onClick={() => !disabled && onPromptClick(prompt)}
            disabled={disabled}
            size="small"
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * All available suggested prompts for new company chats.
 * These relate to the default opening prompt and common questions users might ask.
 */
const ALL_COMPANY_CHAT_PROMPTS: SuggestedPrompt[] = [
  {
    id: 'entry-strategies',
    text: 'What entry strategies have proven effective?',
  },
  {
    id: 'security-requirements',
    text: 'What security requirements does this company typically request?',
  },
  {
    id: 'legal-friction',
    text: 'In what ways has Legal added friction to past deals?',
  },
  {
    id: 'craft-intro',
    text: 'Can you help me craft an introductory message for this company aligns with our product value proposition?',
  },
  {
    id: 'key-stakeholders',
    text: 'Who are the key stakeholders I should engage with?',
  },
];

/**
 * Returns 3 random prompts from the available company chat prompts.
 */
export function getRandomCompanyChatPrompts(count: number = 3): SuggestedPrompt[] {
  const shuffled = [...ALL_COMPANY_CHAT_PROMPTS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export default SuggestedPrompts;
